// src/pages/superadmin/user/ViewUser.jsx
import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Container,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";
import api from "../../../utils/api";

const ROLE_OPTIONS = ["Manager", "Billing Staff"];

const ViewUser = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();
  const superAdmin = JSON.parse(localStorage.getItem("user"));
  const superAdminId = superAdmin?.id;

  useEffect(() => {
    if (superAdminId) fetchUsers();
  }, [superAdminId]);

  const fetchUsers = async () => {
    const res = await api.get(`/api/users/get-all/${superAdminId}`);
    setUsers(res.data);
  };

  const handleEdit = (user) => {
    setSelectedUser({
      _id: user._id,
      name: user.name,
      email: user.email,
      phoneNumber: user.phoneNumber,
      role: user.role,
      password: "", // blank by default
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
  };

  const handleUpdate = async () => {
    try {
      const payload = { ...selectedUser };
      // if password is blank, remove it so backend won't overwrite
      if (!payload.password) delete payload.password;

      await api.put(`/api/users/update/${payload._id}`, payload);
      alert("User updated!");
      fetchUsers();
      handleClose();
    } catch (err) {
      console.error("Update failed:", err);
      alert("Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await api.delete(`/api/users/delete/${id}`);
      fetchUsers();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed");
    }
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5">User Management</Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/super-admin/user/create")}
          >
            Create User
          </Button>
        </Box>

        {/* Edit Dialog */}
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Name"
              value={selectedUser?.name || ""}
              onChange={(e) =>
                setSelectedUser((u) => ({ ...u, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={selectedUser?.email || ""}
              onChange={(e) =>
                setSelectedUser((u) => ({ ...u, email: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Phone Number"
              value={selectedUser?.phoneNumber || ""}
              onChange={(e) =>
                setSelectedUser((u) => ({ ...u, phoneNumber: e.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              label="Role"
              value={selectedUser?.role || ""}
              onChange={(e) =>
                setSelectedUser((u) => ({ ...u, role: e.target.value }))
              }
              fullWidth
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Password"
              type="password"
              value={selectedUser?.password || ""}
              onChange={(e) =>
                setSelectedUser((u) => ({ ...u, password: e.target.value }))
              }
              helperText="Leave blank to keep existing password"
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdate}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Users Table (full width) */}
        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u, i) => (
                  <TableRow key={u._id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.phoneNumber}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleEdit(u)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(u._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </SuperAdminLayout>
  );
};

export default ViewUser;
